import { FC, ReactNode, useEffect } from "react";
import { generatePath, Link } from "react-router-dom";
import { useAppSelector } from "../../../app/store/store";
import { MyButton } from "../../../shared/ui/button";
import { STAFF_USER_ROUTE, STUDENT_ROUTE } from "../../../app/router/routes";
import { ICalendarLesson, TLessonAction, TLessonKind, TLessonStatus } from "../../../entities/calendar";
import { ArrowLeftIcon, BanIcon, CalendarIcon, CheckIcon, ClockIcon, HistoryIcon, 
    RescheduleIcon, StudentIcon, TelegramIcon, TrashIcon, TutorIcon } from "../../../shared/ui/icon";
import classes from './calendarLessonCard.module.scss'
import { formatMoscowDate, formatMoscowTime, isSameMoscowDate } from "../../../shared/lib/helpers/moscowDateTime";

interface IProps {
    lesson: ICalendarLesson;
    startsAt: Date;
    endsAt: Date;
    formatTime: (date: Date) => string;
    onBack: () => void;
    onAction?: (action: TLessonAction) => void;
    onMount?: () => void;
}

const STATUS_LABELS: Record<TLessonStatus, string> = {
    LESSON_STATUS_UNSPECIFIED: 'Неизвестно',
    LESSON_STATUS_SCHEDULED: 'Запланировано',
    LESSON_STATUS_COMPLETED: 'Проведено',
    LESSON_STATUS_CANCELLED: 'Отменено',
    LESSON_STATUS_RESCHEDULED: 'Перенесено',
}

const KIND_LABELS: Record<TLessonKind, string> = {
    LESSON_KIND_UNSPECIFIED: 'Тип занятия не указан',
    LESSON_KIND_REGULAR: 'Регулярное занятие',
    LESSON_KIND_EXTRA: 'Дополнительное занятие',
    LESSON_KIND_TRIAL: 'Пробное занятие',
}

const ACTION_LABELS: Partial<Record<TLessonAction, string>> = {
    LESSON_ACTION_COMPLETE: 'Провести',
    LESSON_ACTION_CANCEL: 'Отменить',
    LESSON_ACTION_RESCHEDULE: 'Перенести',
    LESSON_ACTION_VIEW_HISTORY: 'История',
    LESSON_ACTION_DELETE: 'Удалить',
    LESSON_ACTION_REOPEN_CANCELLED: 'Откатить отмену',
}

const ACTION_ICONS: Partial<Record<TLessonAction, ReactNode>> = {
    LESSON_ACTION_COMPLETE: <CheckIcon />,
    LESSON_ACTION_CANCEL: <BanIcon />,
    LESSON_ACTION_RESCHEDULE: <RescheduleIcon />,
    LESSON_ACTION_VIEW_HISTORY: <HistoryIcon />,
    LESSON_ACTION_DELETE: <TrashIcon />,
    LESSON_ACTION_REOPEN_CANCELLED: <CheckIcon />,
}

const getStatusClass = (status: TLessonStatus): string => {
    if(status === 'LESSON_STATUS_COMPLETED') return classes.completed
    if(status === 'LESSON_STATUS_CANCELLED') return classes.cancelled
    if(status === 'LESSON_STATUS_RESCHEDULED') return classes.rescheduled

    return classes.scheduled
}

const getDateLabel = (date: Date): string => {
    return formatMoscowDate(date, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

const getShortDateLabel = (date: Date): string => {
    return formatMoscowDate(date, {
        day: '2-digit',
        month: '2-digit',
    })
}

const getDateTimeRangeLabel = (startsAt: Date, endsAt: Date, formatTime: (date: Date) => string): string => {
    if(isSameMoscowDate(startsAt, endsAt)){
        return `${formatTime(startsAt)}–${formatTime(endsAt)}`
    }

    return `${getShortDateLabel(startsAt)} ${formatTime(startsAt)}–${getShortDateLabel(endsAt)} ${formatTime(endsAt)}`
}

const getDateTimeLabel = (value: string): string => {
    const date = new Date(value)
    return `${getDateLabel(date)} ${formatMoscowTime(value)}`
}

const getTransferLabel = (lesson: ICalendarLesson): string => {
    const link = lesson.movedTo || lesson.movedFrom
    if(!link?.startsAt) return ''

    const prefix = lesson.movedTo ? 'Перенесено на' : 'Перенесено с'
    return `${prefix} ${getShortDateLabel(new Date(link.startsAt))} ${formatMoscowTime(link.startsAt)}`
}

export const CalendarLessonCard: FC<IProps> = ({lesson, startsAt, endsAt, formatTime, onBack, onAction, onMount}) => {
    const {my} = useAppSelector(state => state.myReducer)
    const hideCancellationDetails = my.role === 'tutor' && lesson.status === 'LESSON_STATUS_CANCELLED'
    const actions = lesson.availableActions.filter(action => Boolean(ACTION_LABELS[action]))
    const lessonTagName = typeof lesson.tag === 'string'
        ? (lesson.tag || 'Без тега')
        : (lesson.tag?.name || 'Без тега')
    const transferLabel = getTransferLabel(lesson)
    const isPaidFaultEvent = lesson.sanctionType === 'student_balance_charge' && lesson.sanctionApplied
    const hasPendingFreeLesson = lesson.sanctionType === 'tutor_free_next_lesson' && !lesson.sanctionApplied

    useEffect(() => {
        onMount?.()
    }, [onMount])
    
    return (
        <section className={classes.container}>
            <button type="button" className={classes.backButton} onClick={onBack}>
                <ArrowLeftIcon />
                Вернуться к расписанию
            </button>

            <section className={classes.header}>
                <section className={classes.metaList}>
                    <span className={classes.metaBadge}>
                        <CalendarIcon size={18} />
                        {getDateLabel(startsAt)}
                    </span>
                    <span className={classes.metaBadge}>
                        <ClockIcon size={18} />
                        {getDateTimeRangeLabel(startsAt, endsAt, formatTime)}
                    </span>
                    <span className={`${classes.metaBadge} ${classes.telegramBadge}`}>
                        <TelegramIcon size={18} />
                        {lesson.tgAccount.username}
                    </span>
                    <span className={`${classes.metaBadge} ${classes.kindBadge}`}>
                        {KIND_LABELS[lesson.kind]}
                    </span>
                    <span className={`${classes.metaBadge} ${classes.lessonIdBadge}`}>
                        ID урока: {lesson.id}
                    </span>
                    {transferLabel && (
                        <span className={`${classes.metaBadge} ${classes.transferBadge}`}>
                            <RescheduleIcon />
                            {transferLabel}
                        </span>
                    )}
                    {isPaidFaultEvent && (
                        <span className={`${classes.metaBadge} ${classes.paidFaultBadge}`}>
                            Платная отмена/перенос
                        </span>
                    )}
                    {hasPendingFreeLesson && (
                        <span className={`${classes.metaBadge} ${classes.freePendingBadge}`}>
                            Следующий урок бесплатно
                        </span>
                    )}
                    {lesson.isFreeBySanction && (
                        <span className={`${classes.metaBadge} ${classes.freeAppliedBadge}`}>
                            Бесплатно по санкции
                        </span>
                    )}
                </section>

                <section className={classes.statusWrap}>
                    <span className={classes.statusLabel}>Статус:</span>
                    <span className={`${classes.statusBadge} ${getStatusClass(lesson.status)}`}>
                        {STATUS_LABELS[lesson.status]}
                    </span>
                </section>
            </section>

            <section className={classes.sections}>
                <section className={classes.personSection}>
                    <h3 className={classes.sectionTitle}>
                        <TutorIcon />
                        Исполнитель
                    </h3>
                    <section className={classes.personCard}>
                        <span className={classes.personIndex}>1</span>
                        <span className={classes.personName}>{lesson.tutor.fullName}</span>
                        {Number(lesson.tutor.userId) > 0 && (
                            <Link
                                className={classes.detailsButton}
                                to={generatePath(STAFF_USER_ROUTE.path, {user_id: lesson.tutor.userId})}
                            >
                                Подробнее
                            </Link>
                        )}
                    </section>
                </section>

                <section className={classes.personSection}>
                    <h3 className={classes.sectionTitle}>
                        <StudentIcon />
                        Ученик
                    </h3>
                    <section className={classes.personCard}>
                        <span className={classes.personIndex}>1</span>
                        <span className={classes.personName}>{lesson.student.fullName}</span>
                        <Link className={classes.detailsButton} to={generatePath(STUDENT_ROUTE.path, {id: lesson.student.id})}>
                            Подробнее
                        </Link>
                    </section>
                </section>
            </section>

            <section className={classes.infoGrid}>
                <section className={classes.infoItem}>
                    <span className={classes.infoLabel}>Тег</span>
                    <span className={classes.infoValue}>{lessonTagName}</span>
                </section>
                {!hideCancellationDetails && lesson.statusComment && (
                    <section className={classes.infoItem}>
                        <span className={classes.infoLabel}>Комментарий</span>
                        <span className={classes.infoValue}>{lesson.statusComment}</span>
                    </section>
                )}
                {lesson.completedAt && (
                    <section className={classes.infoItem}>
                        <span className={classes.infoLabel}>Отметка проведения</span>
                        <span className={classes.infoValue}>{getDateTimeLabel(lesson.completedAt)}</span>
                    </section>
                )}
                {!hideCancellationDetails && lesson.creator && (
                    <section className={classes.infoItem}>
                        <span className={classes.infoLabel}>Создатель</span>
                        {Number(lesson.creator.userId) > 0 ? (
                            <Link
                                className={classes.creatorLink}
                                to={generatePath(STAFF_USER_ROUTE.path, {user_id: lesson.creator.userId})}
                            >
                                {lesson.creator.fullName || `#${lesson.creator.userId}`}
                            </Link>
                        ) : (
                            <span className={classes.infoValue}>{lesson.creator.fullName}</span>
                        )}
                    </section>
                )}
            </section>

            {Boolean(actions.length) && (
                <section className={classes.actionsBlock}>
                    <h3 className={classes.actionsTitle}>Действия с уроком</h3>
                    <section className={classes.actionsList}>
                        {actions.map(action => (
                            <section key={action} className={classes.actionButton}>
                                <MyButton onClick={() => onAction?.(action)}>
                                    <span className={classes.actionButtonContent}>
                                        {ACTION_ICONS[action]}
                                        {ACTION_LABELS[action] ?? action}
                                    </span>
                                </MyButton>
                            </section>
                        ))}
                    </section>
                </section>
            )}
        </section>
    )
}
